import { useState } from 'react'
import { Card, ChannelPreference, EmptyState, NoticeList, Skeleton } from '@/ds'
import { NotAvailable } from '@/app/components/NotAvailable'
import { useLoadedStay } from '@/app/stay/StayProvider'
import { dayMonth, time } from '@/lib/format'
import { useAsync } from '@/lib/useAsync'
import { notificacionesService } from '@/services/notificaciones'
import type { NoticePreference, SentNotice } from '@/services/notificaciones'
import { useSave } from '../Checkin/useSave'
import { SectionError } from '../Estancia/sections/SectionError'
import { BackLink } from '../Pagos/shared'
import { useLang } from '../Reserva/shared'

const sameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString()

function whenText(iso: string, now: Date, lang: string, t: ReturnType<typeof useLang>['t']): string {
  const d = new Date(iso)
  const yesterday = new Date(now)
  yesterday.setDate(now.getDate() - 1)
  if (sameDay(d, now)) return t('v2.avisos.hoy', { time: time(iso, lang) })
  if (sameDay(d, yesterday)) return t('v2.avisos.ayer', { time: time(iso, lang) })
  return dayMonth(iso, lang)
}

/**
 * 8.12 · Avisos y preferencias (Cierre.dc.html notif, Bloque 7 §7c): lo enviado por WhatsApp y email con canal y hora,
 * y preferencias por tipo y canal; llave y fianza no se apagan. Sin notificaciones de la app. Sin endpoints en la API
 * actual: las preferencias no se guardan en el dispositivo porque no evitarían los mensajes (D53).
 */
export function Avisos() {
  const { t, lang } = useLang()
  const stay = useLoadedStay()
  const [now] = useState(() => new Date())
  const [notices, reloadNotices] = useAsync(`avisos:${stay.guestCode}`, (signal) => notificacionesService.list(stay, signal))
  const [prefs, reloadPrefs] = useAsync(`avisos-prefs:${stay.guestCode}`, (signal) => notificacionesService.getPreferences(stay, signal))
  // Con la API actual ni la lista ni las preferencias existen (D53): un solo aviso con su contacto para la pantalla.
  const showUnavailable = notices.kind === 'unsupported' || prefs.kind === 'unsupported'
  const consequences = prefs.kind === 'unsupported' ? [t('v2.noDisponible.consecuencia.avisos')] : []
  return (
    <main className="page">
      <div className="pagos__head">
        <BackLink to="/" label={t('v2.ds.nav.estancia')} />
        <h1>{t('v2.avisos.titulo')}</h1>
        <p className="page__lead">{t('v2.avisos.lead')}</p>
      </div>
      {showUnavailable && <NotAvailable consequences={consequences} />}
      <section className="pagos__group" aria-label={t('v2.avisos.enviados')}>
        {notices.kind === 'loading' && (
          <div aria-busy="true">
            <Skeleton height={64 * 3} />
          </div>
        )}
        {notices.kind === 'error' && <SectionError code={notices.code} onRetry={reloadNotices} />}
        {notices.kind === 'ready' && notices.value.length === 0 && (
          <EmptyState icon="bell" title={t('v2.avisos.vacioTitulo')} headingLevel={2}>
            {t('v2.avisos.vacioTexto')}
          </EmptyState>
        )}
        {notices.kind === 'ready' && notices.value.length > 0 && (
          <Card flush>
            <NoticeList items={notices.value.map((n: SentNotice) => ({ id: n.id, title: n.title, text: n.text, unread: !n.read, meta: t('v2.avisos.meta', { when: whenText(n.sentAt, now, lang, t), channel: t(`v2.ds.channelPreference.${n.channel}`) }) }))} />
          </Card>
        )}
      </section>

      {prefs.kind !== 'unsupported' && (
        <section className="pagos__group" aria-labelledby="avisos-como">
          <h2 id="avisos-como" className="pagos__h2">
            {t('v2.avisos.como')}
          </h2>
          {prefs.kind === 'ready' && <p className="cierre__note">{t('v2.avisos.comoTexto')}</p>}
          {prefs.kind === 'loading' && (
            <div aria-busy="true">
              <Skeleton height={64 * 5} />
            </div>
          )}
          {prefs.kind === 'error' && <SectionError code={prefs.code} onRetry={reloadPrefs} />}
          {prefs.kind === 'ready' && <Preferences initial={prefs.value} />}
        </section>
      )}
    </main>
  )
}

function Preferences({ initial }: { initial: NoticePreference[] }) {
  const { t } = useLang()
  const stay = useLoadedStay()
  const [prefs, setPrefs] = useState(initial)
  const [saved, setSaved] = useState(false)
  const { error, run } = useSave()
  const subtitle = (p: NoticePreference) => (p.locked ? t('v2.avisos.noSeApaga') : p.type === 'registration_pending' || p.type === 'arrival_tomorrow' || p.type === 'extras' ? t(`v2.avisos.tipo.${p.type}Sub`) : undefined)
  return (
    <>
      <Card flush>
        <ChannelPreference
          rows={prefs.map((p) => ({ id: p.type, title: t(`v2.avisos.tipo.${p.type}`), ...(subtitle(p) ? { subtitle: subtitle(p) } : {}), whatsapp: p.whatsapp, email: p.email, locked: p.locked }))}
          onChange={(id, channel, on) => {
            setSaved(false)
            void run(() => notificacionesService.setPreference(stay, id as NoticePreference['type'], channel, on)).then((next) => {
              if (next) {
                setPrefs(next)
                setSaved(true)
              }
            })
          }}
        />
      </Card>
      <p role="status" className="cierre__note">
        {saved ? t('v2.avisos.guardado') : ''}
      </p>
      {error && (
        <p className="pagos__error" role="alert">
          {error}
        </p>
      )}
    </>
  )
}
